import React, { useState, useEffect } from 'react'; 
import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, TextField, Button, Alert, Typography, Paper, CircularProgress, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BASE_URL } from '../utils/endpoints';

const theme = createTheme({
    palette: {
        primary: {
            main: '#24803F', 
            light: '#4CAF50',
            dark: '#1A602F',
        },
        secondary: { 
            main: '#FFA726', 
        },
        background: {
            default: '#F5F7F8', 
            paper: '#FFFFFF',   
        },
        text: {
            primary: '#333333',
            secondary: '#666666',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h5: {
            fontWeight: 700,
            fontSize: '1.75rem',
            color: '#333333', 
        },
        body2: {
            fontSize: '0.9rem',
            color: '#666666',
        },
    },
    components: {
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px', 
                        backgroundColor: '#F9FAFB', 
                        '& fieldset': {
                            borderColor: '#E0E0E0',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#24803F',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#24803F',
                            borderWidth: '2px',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#666666',
                        '&.Mui-focused': {
                            color: '#24803F', 
                        },
                    },
                    '& .MuiInputBase-input': { 
                        color: '#333333',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '10px', 
                    padding: '14px 0', 
                    fontSize: '1rem',
                    boxShadow: '0 4px 12px rgba(36, 128, 63, 0.2)', 
                    transition: 'all 0.3s ease', 
                    '&:hover': {
                        boxShadow: '0 6px 16px rgba(36, 128, 63, 0.3)', 
                        transform: 'translateY(-2px)', 
                        backgroundColor: '#1A602F', 
                    },
                    '&:active': { 
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 4px rgba(36, 128, 63, 0.1)',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#E0E0E0', 
                        color: '#999999', 
                        boxShadow: 'none',
                    }
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '18px', 
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)', 
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: '10px', 
                    alignItems: 'center', 
                },
                message: {
                    fontWeight: 500, 
                }
            }
        }
    },
});

const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease: 'easeOut' } },
};

const buttonVariants = {
    hover: { scale: 1.02, transition: { duration: 0.2 } },
    tap: { scale: 0.98 },
};

const alertVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

const otpSentMessageVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { opacity: 1, height: 'auto', transition: { duration: 0.5, ease: 'easeOut' } },
    exit: { opacity: 0, height: 0, transition: { duration: 0.3, ease: 'easeOut' } }
};

function Login() {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [otpMessage, setOtpMessage] = useState('');
    const navigate = useNavigate();

    const handleSendOtp = async () => {
        setErrorMessage('');
        setOtpMessage('');
        if (!email.trim()) {
            setErrorMessage('Please enter your email address.');
            return;
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            setErrorMessage('Please enter a valid email format (e.g., example@domain.com).');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${BASE_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (!response.ok) {
                setErrorMessage(data.error || 'Failed to send OTP. Please check your email address.');
            } else {
                setOtpSent(true);
                setOtpMessage('OTP sent successfully! Please check your email inbox.');
            }
        } catch (error) {
            setErrorMessage('Network error occurred. Please check your internet connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        setErrorMessage('');
        setOtpMessage('');

        if (!otp.trim()) {
            setErrorMessage('Please enter the OTP you received.');
            return;
        }
        if (otp.length !== 6 || !/^\d+$/.test(otp)) {
            setErrorMessage('OTP must be a 6-digit number.');
            return;
        }

        setLoading(true);
        try {
            const verifyResponse = await fetch(`${BASE_URL}/verify-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp }),
            });

            const verifyData = await verifyResponse.json();
            if (!verifyResponse.ok) {
                setErrorMessage(verifyData.error || 'Invalid OTP. Please try again.');
                setLoading(false); 
                return; 
            }

            const authToken = verifyData.token;
            localStorage.setItem('authToken', authToken);

            const profileResponse = await fetch(`${BASE_URL}/profile`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`, 
                },
            });

            const profileData = await profileResponse.json();

            if (!profileResponse.ok) {
                setErrorMessage(profileData.error || 'Failed to fetch user profile after login. Please try again.');
                localStorage.removeItem('authToken'); 
            } else {
                const userRole = profileData.user?.role || profileData.role; 

                if (userRole !== 'user' ) {
                    navigate('/');
                    window.location.reload();
                } else {
                    setErrorMessage('You do not have administrative access. Please log in with an admin account.');
                    localStorage.removeItem('authToken'); 
                }
            }
        } catch (error) {
            setErrorMessage('Authentication failed due to a network or server issue. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleEmailKeyPress = (e) => {
        if (e.key === 'Enter' && !otpSent && !loading) {
            handleSendOtp();
        }
    };

    const handleOtpKeyPress = (e) => {
        if (e.key === 'Enter' && otpSent && !loading) {
            handleVerifyOtp();
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg,rgb(5, 99, 33) 0%,rgb(97, 225, 101) 100%)',
                    p: 0,
                    m: 0,
                    height: '98vh',
                    position: 'relative',
                    overflow: 'hidden',
                    scrollBehavior: 'no-scroll',
                    // Hide scrollbars for all browsers
                    '&::-webkit-scrollbar': { display: 'none' },
                    '-ms-overflow-style': 'none',
                    'scrollbarWidth': 'none',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `
                            linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: '150px 150px',
                        zIndex: 1,
                        opacity: 0.6,
                    }}
                />
                <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    style={{ zIndex: 2 }}
                >
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 3, sm: 5 },
                            maxWidth: 450,
                            width: '100%',
                            borderRadius: '18px',
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                        }}
                    >
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box
                                sx={{
                                    width: 90,
                                    height: 90,
                                    borderRadius: '16px',
                                    margin: '0 auto 24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <img
                                    src="/Fevicon_newcolor.png"
                                    alt="SocioGlamm Logo"
                                    style={{ width: '80%', height: '80%', objectFit: 'contain' }}
                                />
                            </Box>
                            <Typography variant="h5" component="h1" sx={{ mb: 1, color: theme.palette.text.primary }}>
                                Welcome Back!
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                {otpSent ? 'Enter the 6-digit OTP sent to your email' : 'Sign in to your account with your email'}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={handleEmailKeyPress}
                                placeholder="e.g., you@example.com"
                                required
                                variant="outlined"
                                InputProps={{
                                    startAdornment: <EmailIcon sx={{ color: 'primary.dark', mr: 1 }} />,
                                }}
                                sx={{ mb: 2 }}
                                disabled={otpSent && loading}
                            />

                            {otpSent && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3, duration: 0.5 }}
                                >
                                    <TextField
                                        fullWidth
                                        label="One-Time Password (OTP)"
                                        type={showOtp ? 'text' : 'password'}
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        onKeyDown={handleOtpKeyPress}
                                        placeholder="Enter 6-digit OTP"
                                        required
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: <LockIcon sx={{ color: 'primary.dark', mr: 1 }} />,
                                            endAdornment: (
                                                <IconButton
                                                    aria-label="toggle OTP visibility"
                                                    onClick={() => setShowOtp(!showOtp)}
                                                    edge="end"
                                                >
                                                    {showOtp ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </motion.div>
                            )}
                        </Box>

                        <motion.div
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={otpSent ? handleVerifyOtp : handleSendOtp}
                                disabled={loading}
                                sx={{ py: 1.5 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    otpSent ? 'Verify OTP & Log In' : 'Send OTP to Email'
                                )}
                            </Button>
                        </motion.div>

                        {otpMessage && otpSent && (
                            <motion.div
                                variants={otpSentMessageVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <Alert
                                    severity="success"
                                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                                    sx={{ mt: 2, borderRadius: '10px' }}
                                >
                                    {otpMessage}
                                </Alert>
                            </motion.div>
                        )}

                        {errorMessage && (
                            <motion.div
                                variants={alertVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <Alert
                                    severity="error"
                                    sx={{
                                        mt: 2,
                                        borderRadius: '10px',
                                        '& .MuiAlert-icon': {
                                            color: theme.palette.error.main,
                                        },
                                    }}
                                >
                                    {errorMessage}
                                </Alert>
                            </motion.div>
                        )}
                    </Paper>
                </motion.div>
            </Box>
        </ThemeProvider>
    );
}

export default Login;